/**
 * Protected spots that cannot be merged, renamed, or edited.
 * These are internal/system spots that must remain unchanged.
 */

// Unknown/Internal spot - used for sessions without a valid spot assignment
export const UNKNOWN_SPOT_ID = 12783;

// Set of all protected spot IDs
export const PROTECTED_SPOT_IDS = new Set<number>([UNKNOWN_SPOT_ID]);

/**
 * Check if a spot is protected from modifications
 * @param spotId - The spot ID to check
 * @returns true if the spot is protected
 */
export const isProtectedSpot = (spotId: number | undefined | null): boolean => {
  if (spotId === undefined || spotId === null) {
    return false;
  }
  return PROTECTED_SPOT_IDS.has(spotId);
};

/**
 * Check if any spot in an array is protected
 * @param spotIds - Array of spot IDs to check
 * @returns The first protected spot ID found, or null if none
 */
export const findProtectedSpot = (spotIds: number[]): number | null => {
  for (const spotId of spotIds) {
    if (isProtectedSpot(spotId)) {
      return spotId;
    }
  }
  return null;
};

/**
 * Get a user-friendly warning message for protected spots
 * @param spotId - The protected spot ID
 * @returns Warning message string
 */
export const getProtectedSpotWarning = (spotId: number): string => {
  return `Spot ${spotId} is a protected system spot and cannot be modified.`;
};

/**
 * Error code returned by the backend for protected spot operations
 */
export const PROTECTED_SPOT_ERROR_CODE = 'PROTECTED_SPOT';

/**
 * Interface for protected spot error response from backend
 */
export interface ProtectedSpotError {
  error: typeof PROTECTED_SPOT_ERROR_CODE;
  message: string;
  spotId: number;
}

/**
 * Type guard to check if an error is a protected spot error
 * @param error - The error object to check
 * @returns true if error is a ProtectedSpotError
 */
export const isProtectedSpotError = (error: unknown): error is ProtectedSpotError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    (error as ProtectedSpotError).error === PROTECTED_SPOT_ERROR_CODE
  );
};
